import { Dropdown as NextDropdown } from '@nextui-org/react';
import React, { Key, ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InfiniteScroll from 'react-infinite-scroll-component';

import { ChevronIcon } from '~/assets';
import { ShimmerImage, ShimmerText } from '~/components';
import { DropdownItemProps, DropDownProps } from '~/components/Dropdown/Dropdown';
import {
  Container,
  DropdownError,
  DropDownItemContainer,
  DropDownItemDescription,
  DropDownItemLeftContainer,
  DropDownItemLoader,
  DropDownItemText,
  Separator,
  Title,
} from '~/components/Dropdown/Dropdown.styles';
import Badge from '~/components/FilterBadge';
import { useDebounce } from '~/hooks/useDebounce';

import { parsePx } from '../../hooks/useDimensions';
import useDropdown from '../Dropdown/useDropdown';
import {
  BottomFade,
  HeaderScrollArea,
  NoResults,
  StyledDropdown,
  StyledDropDownButton,
  StyledDropDownMenu,
  StyledInput,
  StyledSearchIcon,
} from './InfiniteScrollDropdown.styles';

const KeyNoResults = 'noResults';
const scrollViewId = 'scrollableDiv';

export type InfiniteScrollDropdownSelection = number | number[];

export interface InfiniteScrollProps {
  hasMore: boolean;
  loadMore: () => void;
  searchOrgInfiniteScroll: string;
  setSearchOrgInfiniteScroll: (value: string) => void;
  loadingFirst: boolean;
  loadingMore: boolean;
}

export interface InfiniteScrollDropdownProps<T = any> extends DropDownProps<T> {
  prefilledValue?: string;
  shouldApplyRoundCorners?: boolean;
  variant?: 'header';
  // Method-shorthand (not an arrow-typed property) so TS uses bivariant parameter
  // checking here — needed because styled(InfiniteScrollDropdown) erases the generic T,
  // which would otherwise make this prop's type incompatible across dropdown consumers.
  renderItem?(this: void, item: DropdownItemProps<T>, index: number): ReactNode;
  // Receives a `close` callback so footer actions (e.g. "Create audience") can dismiss the
  // dropdown before opening their own overlay, instead of rendering on top of it.
  footer?: (close: () => void) => ReactNode;
  searchPlaceholder?: string;
}

// eslint-disable-next-line comma-spacing
const InfiniteScrollDropdown = <T,>(props: InfiniteScrollDropdownProps<T> & InfiniteScrollProps) => {
  const {
    title,
    required,
    items,
    disabledKeys,
    disallowEmptySelection,
    selectedIndex,
    placeholder,
    error,
    onSelectionChange,
    prefilledValue,
    placeholderIcon,
    showBadge,
    hasMore,
    loadMore,
    searchOrgInfiniteScroll,
    setSearchOrgInfiniteScroll,
    loadingFirst,
    loadingMore,
    shouldApplyRoundCorners = false,
    showSelectedValue,
    disabled,
    variant,
    renderItem,
    footer,
    searchPlaceholder,
    ...rest
  } = props;
  const [searchTextLocal, setSearchTextLocal] = useState(searchOrgInfiniteScroll || '');
  const searchTextDebounce = useDebounce(searchTextLocal, 500) as string;
  const { t } = useTranslation();

  const [maxHeight, setMaxHeight] = useState('20rem');
  const {
    selected,
    setSelected,
    selectedValue: selectedValueDropDown,
    selectedIcon,
    disabledKeysLocal,
    buttonRef,
    buttonWidth,
  } = useDropdown(props);

  // Close the menu by toggling the trigger (the footer lives inside the popover, so it's only
  // rendered while open). We can't control `isOpen`: NextUI wires our onOpenChange to both the
  // menu-trigger state and the Popover's outside-click dismiss, so the opening click's spurious
  // close would slam a controlled menu shut.
  const closeMenu = useCallback(() => buttonRef.current?.click(), [buttonRef]);

  useEffect(() => {
    setSearchOrgInfiniteScroll?.(searchTextDebounce);
  }, [searchTextDebounce]);

  // The prefilled value is used to display the value of the dropdown when the user filtered
  // in the orgs page but we eventually don't have the item loaded.
  const selectedValue = useMemo(() => selectedValueDropDown || prefilledValue, [selectedValueDropDown, prefilledValue]);

  const onLocalSelectionChange = useCallback(
    (keys: 'all' | Set<Key>) => {
      if (keys === 'all' || (keys?.size === 1 && Array.from(keys)?.[0] === KeyNoResults)) return;
      setSelected(keys);
      const selectedIndexes = Array.from(keys || []).map((key) => Number(key));
      onSelectionChange?.(selectedIndexes[0]);
    },
    [onSelectionChange, setSelected],
  );

  const listShimmer = useMemo(
    () =>
      Array.from({ length: 10 }, (_, index) => (
        <NextDropdown.Item key={`loader-${index.toString()}`} textValue={`loader-${index.toString()}`}>
          <DropDownItemLoader>
            <ShimmerImage width="1.5rem" height="1.5rem" />
            <DropDownItemText>
              <ShimmerText width="7rem" />
            </DropDownItemText>
          </DropDownItemLoader>
        </NextDropdown.Item>
      )),
    [],
  );

  const recalculateMaxHeight = () => {
    // get the height of the element with class name 'nextui-popover-content'
    const element = document.getElementsByClassName('nextui-popover-content-container')[0] as HTMLElement;

    if (!element) return;

    const height = parsePx(element.style.maxHeight);

    // We need to be able to trigger pagination
    let maxHeight = height / 16 - 3.25;
    if (maxHeight > 21) maxHeight = 21;

    setMaxHeight(`${maxHeight}rem`);
  };

  useEffect(() => {
    recalculateMaxHeight();

    // Listen for changes in the popover and recalculate the max height
    window.addEventListener('resize', recalculateMaxHeight);
    window.addEventListener('scroll', recalculateMaxHeight);

    // Cleanup the event listener on component unmount
    return () => {
      window.removeEventListener('resize', recalculateMaxHeight);
      window.removeEventListener('scroll', recalculateMaxHeight);
    };
  }, []);

  const dropDownComponent = useMemo(
    () => (
      <StyledDropDownMenu
        width={buttonWidth}
        $maxHeight={maxHeight}
        $variant={variant}
        selectionMode="single"
        disallowEmptySelection={disallowEmptySelection}
        selectedKeys={selected}
        disabledKeys={disabledKeysLocal}
        onSelectionChange={onLocalSelectionChange}
        id={scrollViewId}
      >
        {items
          .map((item, index) => (
            <NextDropdown.Item key={index.toString()} textValue={index.toString()}>
              {renderItem ? (
                renderItem(item, index)
              ) : (
                <DropDownItemContainer>
                  <DropDownItemLeftContainer>
                    {item.icon}
                    <DropDownItemText>{item.text}</DropDownItemText>
                  </DropDownItemLeftContainer>
                  {item.description && <DropDownItemDescription>{item.description}</DropDownItemDescription>}
                </DropDownItemContainer>
              )}
              {!renderItem && index !== items.length - 1 && <Separator />}
            </NextDropdown.Item>
          ))
          .concat(
            loadingFirst || loadingMore ? (
              listShimmer
            ) : !items.length ? (
              <NextDropdown.Item key={KeyNoResults} textValue={t('common.noResults')}>
                <NoResults>{t('common.noResults')}</NoResults>
              </NextDropdown.Item>
            ) : null,
          )}
      </StyledDropDownMenu>
    ),
    [
      buttonWidth,
      disabledKeysLocal,
      disallowEmptySelection,
      items,
      listShimmer,
      loadingFirst,
      loadingMore,
      maxHeight,
      onLocalSelectionChange,
      renderItem,
      selected,
      t,
      variant,
    ],
  );

  const infiniteScrollComponent = useMemo(() => {
    const isHeaderVariant = variant === 'header';
    const scrollList = (
      <InfiniteScroll
        dataLength={items.length}
        next={() => loadMore()}
        hasMore={hasMore}
        hasChildren
        scrollThreshold="20px"
        scrollableTarget={scrollViewId}
        loader={null}
      >
        {dropDownComponent}
      </InfiniteScroll>
    );

    return (
      <>
        <StyledInput
          placeholder={searchPlaceholder ?? searchTextLocal}
          aria-label={t('common.search')}
          iconLeft={<StyledSearchIcon />}
          clearable
          onChange={(e: any) => setSearchTextLocal(e.target.value as string)}
          value={searchTextLocal}
          removeMargin
          containerMarginTop="0.75rem"
          marginBottom="0.25rem"
        />
        {isHeaderVariant ? (
          <HeaderScrollArea>
            {scrollList}
            <BottomFade />
          </HeaderScrollArea>
        ) : (
          scrollList
        )}
        {footer?.(closeMenu)}
      </>
    );
  }, [
    closeMenu,
    dropDownComponent,
    footer,
    hasMore,
    items.length,
    loadMore,
    searchPlaceholder,
    searchTextLocal,
    t,
    variant,
  ]);

  return (
    <Container {...rest}>
      {title && (
        <Title>
          {title}
          {required && ' *'}
        </Title>
      )}
      <StyledDropdown
        isBordered
        placement={variant === 'header' ? 'bottom-left' : undefined}
        offset={variant === 'header' ? 2 : undefined}
        onOpenChange={(isOpen) => {
          if (isOpen) {
            setTimeout(() => {
              recalculateMaxHeight();
              // Wait for the dropdown to open
            }, 100);
          }
        }}
      >
        <StyledDropDownButton
          disabled={disabled}
          shouldApplyRoundCorners={shouldApplyRoundCorners}
          isBordered={variant !== 'header'}
          $variant={variant}
          // Disable the press ripple/drip on the header variant (brief blue flash on click).
          ripple={variant !== 'header'}
          ref={buttonRef}
          iconRight={<ChevronIcon />}
          error={error}
        >
          <DropDownItemLeftContainer>
            {selectedValue && (showSelectedValue || !showBadge) ? (
              <>
                {selectedIcon}
                {selectedValue}
              </>
            ) : (
              <>
                {placeholderIcon}
                {placeholder || ''}
              </>
            )}
            {showBadge && <Badge>1</Badge>}
          </DropDownItemLeftContainer>
        </StyledDropDownButton>

        {infiniteScrollComponent}
      </StyledDropdown>
      {error && <DropdownError>{error}</DropdownError>}
    </Container>
  );
};

export default InfiniteScrollDropdown;
